import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { Vehicle } from '@commons/models/domain/Vehicle';
import { VehicleControlIndicator } from '@commons/models/domain/VehicleControlIndicator';
import { VehicleStatusEnum } from '@commons/models/domain/VehicleStatusEnum';
import { IBlockerInfo, IVehicleHistoryLock, VehicleBlockerValue, VehicleHistoryLockType, VehicleLock } from '@commons/models/remote.request/VehicleLock';
import { VehicleService } from '@commons/services/vehicle/vehicle.service';
import { WebAccessService } from '@commons/services/web.access/web-access.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalWrapperComponent } from '@shared/components/modal-wrapper/modal-wrapper.component';
import { PermissionService } from '@shared/services/permission.service';
import { NewWebSocketHttpService } from '@shared/websocket/new-web-socket-http.service';

import { Subject, Subscription, debounceTime, switchMap } from 'rxjs';

@Component({
  selector: 'app-th-vehicle-details-locks-tab',
  templateUrl: './th-vehicle-details-locks-tab.component.html',
  styleUrls: ['./th-vehicle-details-locks-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class ThVehicleDetailsLocksTabComponent implements OnInit, OnDestroy {

  @Input('vehicle') public set onChangeVehicle(data: Vehicle) {
    if (data?.Id !== this._vehicle?.Id) {
      this.onChangeLockStateWebSocketSubsc?.unsubscribe();
      this.onChangeVehicleData(data.Id);
    }
    this._vehicle = { ...data } as any;
    this.setLastUpdateDate();
  }

  public _vehicle: Vehicle;

  public blockedOutFirst?: boolean;
  public blockedOutSecond?: boolean;
  public blockedOutFirstConfirmValue?: boolean;
  public blockedOutSecondConfirmValue?: boolean;
  
  public isLoading: boolean = true;
  public isHiddenFirstLock: boolean;
  public isHiddenSecondLock: boolean;
  public userCanChangeStateBlocker: boolean;
  public historyLock: IVehicleHistoryLock[] = [];
  public isChaningOUT1Lock: boolean;
  public isChaningOUT2Lock: boolean;
  public isLoadingHistoryLock: boolean = true;

  public lastUpdated?: any;

  public onChangeOutFirst: Subject<boolean> = new Subject();
  public onChangeOutSecond: Subject<boolean> = new Subject();

  private ComponentSubsc: Subscription = new Subscription();
  public readonly VehicleStatusEnum = VehicleStatusEnum;
  public readonly VehicleHistoryLockType = VehicleHistoryLockType;
  private onChangeVehicleDataSubsc: Subscription;
  private onChangeLockStateWebSocketSubsc: Subscription;

  constructor(
    private webAccessService: WebAccessService,
    public dialog: MatDialog,
    private permissionService: PermissionService,
    private newWebSocketHttpService: NewWebSocketHttpService,
    private vehicle: VehicleService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.checkIfUserCanChangeStateBlocker();
    this.getStateVehicleLock();
    this.onChangeVehicleLockState();
    this.getHistoryVehicleLock();
    this.onChangeLockStateVehicle();
  }

  ngOnDestroy(): void {
    this.ComponentSubsc.unsubscribe();
    this.onChangeVehicleDataSubsc?.unsubscribe();
    this.onChangeLockStateWebSocketSubsc?.unsubscribe();
  }

  private setLockOnEventChangeVehicleData(outName: string): void {
    const blockerValueOut1 = this._vehicle.ControlIndicators?.find(item => item.Id === this._vehicle.blockerParametrId);
    const blockerValueOut2 = this._vehicle.ControlIndicators?.find(item => item.Id === this._vehicle.blockerParametrIdOut2);
    this.blockedOutFirst = blockerValueOut1?.LV?.V;
    this.blockedOutSecond = blockerValueOut2?.LV?.V;
    this.blockedOutFirstConfirmValue = this.blockedOutFirst;
    this.blockedOutSecondConfirmValue = this.blockedOutSecond;

    if (outName === VehicleLock.OUT1) this.isChaningOUT1Lock = false;
    if (outName === VehicleLock.OUT2) this.isChaningOUT2Lock = false;
  }

  private onChangeLockStateVehicle(): void {
    this.onChangeLockStateWebSocketSubsc = this.newWebSocketHttpService.eventChangeVehicleData.subscribe(
      (res) => {
        const vehicleId = res?.Data?.Id;
        const outName = res?.Data?.O;

        if (vehicleId === this._vehicle.Id) {
          setTimeout(() => {
            this.setLockOnEventChangeVehicleData(outName);
            this.getHistoryVehicleLock();
          }, 1000);
        }
      }
    );
  }

  private onChangeVehicleData(idVehicle: number): void {
    this.onChangeVehicleDataSubsc?.unsubscribe();
    this.onChangeVehicleDataSubsc = this.newWebSocketHttpService.updatedVehicles
      .pipe(
        debounceTime(1000),
        switchMap(() => this.vehicle.getDetails$(idVehicle)),
      )
      .subscribe(
        (dataVehicle) => {
          this._vehicle = { ...dataVehicle } as any;
          this.setLastUpdateDate();
        }
      );
  }

  private showModalConfirmLock(data: any): void {
    const dialog = this.dialog.open(ModalWrapperComponent, { width: '100%', height: '100%', maxWidth: '100%', data });
    const sub = dialog.afterClosed().subscribe(
      (res) => {
        if (!res) {
          if (data.OutName === VehicleLock.OUT1) this.blockedOutFirst = !data.Value;
          if (data.OutName === VehicleLock.OUT2) this.blockedOutSecond = !data.Value;
          return;
        }
        if (data.OutName === VehicleLock.OUT1) this.isChaningOUT1Lock = true;
        if (data.OutName === VehicleLock.OUT2) this.isChaningOUT2Lock = true;
      }
    );
    this.ComponentSubsc.add(sub);
  }

  private getStateVehicleLock(): void {
    const sub = this.webAccessService.GetLockVehicleState(this._vehicle.Id).subscribe(
      (data) => {
        this.onGetVehicleLockState(data.BlockerInfos);
      }
    );
    this.ComponentSubsc.add(sub);
  }

  private getHistoryVehicleLock(): void {
    const getTitleTranslate = (type: VehicleHistoryLockType): string => {
      return this.translate.instant(`historyLockType-${type}`);
    };
    const sub = this.webAccessService.GetIgnitionBlockerAuditTrailForVehicle(this._vehicle.Id).subscribe(
      ({ Entries }) => {
        this.historyLock = Entries.map(item => ({
          ...item,
          Timestamp: `${item.Timestamp}Z`,
          Description: getTitleTranslate(item.EntryType),
        }));
        this.isLoadingHistoryLock = false;
      }
    );
    this.ComponentSubsc.add(sub);
  }

  private onGetVehicleLockState(data: IBlockerInfo[]): void {
    const outFirstValue = data.find(item => item.OutName === VehicleLock.OUT1);
    const outSecondValue = data.find(item => item.OutName === VehicleLock.OUT2);
    this.isHiddenFirstLock = outFirstValue ? false : true;
    this.isHiddenSecondLock = outSecondValue ? false : true;
    this.blockedOutFirst = outFirstValue?.BlockerValue === VehicleBlockerValue.On;
    this.blockedOutSecond = outSecondValue?.BlockerValue === VehicleBlockerValue.On;
    this.isChaningOUT1Lock = outFirstValue?.BlockerValue === VehicleBlockerValue.ChangePending;
    this.isChaningOUT2Lock = outSecondValue?.BlockerValue === VehicleBlockerValue.ChangePending;
    this.blockedOutFirstConfirmValue = this.blockedOutFirst;
    this.blockedOutSecondConfirmValue = this.blockedOutSecond;
    this.isLoading = false;
  }

  private onChangeVehicleLockState(): void {
    const subFirst = this.onChangeOutFirst.subscribe(
      (data) => {
        const body = {
          VehicleId: this._vehicle.Id,
          OutName: VehicleLock.OUT1,
          Value: data,
        };
        this.showModalConfirmLock(body);
      }
    );
    this.ComponentSubsc.add(subFirst);

    const subSecond = this.onChangeOutSecond.subscribe(
      (data) => {
        const body = {
          VehicleId: this._vehicle.Id,
          OutName: VehicleLock.OUT2,
          Value: data,
        };
        this.showModalConfirmLock(body);
      }
    );
    this.ComponentSubsc.add(subSecond);
  }

  private setLastUpdateDate(): void {
    if (this._vehicle) {
        const v = this._vehicle;
        const vehicleIndicators: VehicleControlIndicator[] = v.ControlIndicators as VehicleControlIndicator[];
        const paramPosition: VehicleControlIndicator | undefined = vehicleIndicators?.find(x => x.Tp == 22);
        if (paramPosition && paramPosition.LV) {
            const lastPositionTime:Date = new Date(paramPosition.LV.T);
            const n = new Date();
            this.lastUpdated = Math.ceil((n.getTime() - lastPositionTime.getTime()) / 1000);
        }
    }
  }

  private checkIfUserCanChangeStateBlocker(): void {
    this.userCanChangeStateBlocker = this.permissionService.hasBlockerReadWrite();
  }

}
